import React, { useCallback, useState } from 'react';

import { Box } from '../Box';
import { TextArea } from '../Form/TextArea';
import { Icon } from '../Icon';
import TagChip from '../TagChip/TagChip';
import { Heading } from '../Typography';
import { Text } from '../Typography';
import {
  chipsContainerStyles,
  containerStyles,
  headerStyles,
  inputWrapperStyles,
  textAreaStyles,
} from './ListingChat.styles';

export type ListingChatProps = {
  /**
   * Called when the user submits text
   */
  onSubmit: (value: string) => void;
  /**
   * Placeholder text for the input area
   */
  placeholder?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Optional label next to the AI Magic icon
   */
  title?: string;
  /**
   * Array of suggested tags/questions to display as chips
   */
  tags?: string[];
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
};

const ListingChat = ({
  onSubmit,
  placeholder = 'Ask anything about this listing…',
  className,
  title = 'Have a question?',
  tags = [],
  disabled = false,
  ...rest
}: ListingChatProps) => {
  const [value, setValue] = useState('');

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue('');
  }, [onSubmit, value]);

  const handleTagClick = useCallback(
    (tag: string) => () => {
      const trimmed = tag.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
    },
    [onSubmit]
  );

  return (
    <Box css={containerStyles} className={className} {...rest}>
      <Box css={headerStyles}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Heading size="2xs" fontWeight="bold">
            {title}
          </Heading>
          <Box display="flex" alignItems="center" gap="var(--spacing-1)">
            <Icon variant={'AiMagic'} size="medium" />
            <Text size="sm">Beta</Text>
          </Box>
        </Box>
        <Text size="md" textWrap="wrap">
          Get instant answers with Buck, our AI powered assistant.
        </Text>
      </Box>

      <Box css={inputWrapperStyles}>
        <TextArea
          rows={1}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          showSubmit
          onSubmit={handleSubmit}
          disabled={disabled}
          submitDisabled={!value.trim()}
          submitAriaLabel="Submit message"
          css={textAreaStyles}
        />
      </Box>

      {tags.length > 0 && (
        <>
          <Text as="div" size="sm" fontWeight="bold">
            Try one of these
          </Text>
          <Box css={chipsContainerStyles}>
            {tags.map((tag) => (
              <button
                key={tag}
                onClick={handleTagClick(tag)}
                disabled={disabled}
              >
                <TagChip>{tag}</TagChip>
              </button>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ListingChat;
